// src/OHLCBox.js
import React, { useEffect, useRef } from "react";

const EMPTY_TEXT = "Hover candles...";
const BOX_STYLE = {
  whiteSpace: "pre",
  contain: "layout paint style",
};

const normalizeTime = (time) => {
  if (time == null) return null;
  if (typeof time === "number") {
    return time > 1e11 ? Math.floor(time / 1000) : Math.floor(time);
  }
  if (typeof time === "object" && typeof time.timestamp === "number") {
    return Math.floor(time.timestamp);
  }
  const parsed = Date.parse(String(time));
  return Number.isFinite(parsed) ? Math.floor(parsed / 1000) : null;
};

const formatPrice = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : "--";
};

const formatCandleText = (candle) =>
  `O: ${formatPrice(candle.open)}\nH: ${formatPrice(candle.high)}\nL: ${formatPrice(candle.low)}\nC: ${formatPrice(candle.close)}`;

const getParamCandle = (param, series) => {
  const fromSeriesData = param?.seriesData?.get?.(series);
  if (
    fromSeriesData &&
    fromSeriesData.open != null &&
    fromSeriesData.high != null &&
    fromSeriesData.low != null &&
    fromSeriesData.close != null
  ) {
    return fromSeriesData;
  }
  return null;
};

const findClosestIndex = (arr, time, lastIndexRef) => {
  if (!arr || arr.length === 0 || time == null) return -1;
  const n = arr.length;
  const last = typeof lastIndexRef.current === "number" ? lastIndexRef.current : -1;

  if (last >= 0 && last < n) {
    const lastTime = arr[last].time;
    if (lastTime === time) return last;

    const stepLimit = 512;
    let steps = 0;
    if (time > lastTime) {
      let i = last;
      while (i + 1 < n && arr[i + 1].time <= time && ++steps <= stepLimit) i++;
      if (steps <= stepLimit) {
        const cand0 = i;
        const cand1 = Math.min(n - 1, i + 1);
        return Math.abs(arr[cand0].time - time) <= Math.abs(arr[cand1].time - time) ? cand0 : cand1;
      }
    } else {
      let i = last;
      while (i - 1 >= 0 && arr[i - 1].time >= time && ++steps <= stepLimit) i--;
      if (steps <= stepLimit) {
        const cand1 = i;
        const cand0 = Math.max(0, i - 1);
        return Math.abs(arr[cand0].time - time) <= Math.abs(arr[cand1].time - time) ? cand0 : cand1;
      }
    }
  }

  let lo = 0;
  let hi = n - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const midTime = arr[mid].time;
    if (midTime === time) return mid;
    if (midTime < time) lo = mid + 1;
    else hi = mid - 1;
  }

  const cand1 = Math.max(0, Math.min(n - 1, lo));
  const cand0 = Math.max(0, Math.min(n - 1, lo - 1));
  return Math.abs(arr[cand0].time - time) <= Math.abs(arr[cand1].time - time) ? cand0 : cand1;
};

function OHLCBox({ chart, series, dataRef }) {
  const boxRef = useRef(null);
  const rafRef = useRef(null);
  const pendingParamRef = useRef(null);
  const lastIndexRef = useRef(-1);
  const lastTimeRef = useRef(null);
  const lastTextRef = useRef(EMPTY_TEXT);
  const hasValueRef = useRef(false);

  useEffect(() => {
    const el = boxRef.current;
    if (el) {
      el.textContent = EMPTY_TEXT;
      lastTextRef.current = EMPTY_TEXT;
    }
  }, []);

  useEffect(() => {
    if (!chart || !dataRef) return;

    const writeText = (text) => {
      if (text === lastTextRef.current) return;
      const el = boxRef.current;
      if (!el) return;
      el.textContent = text;
      lastTextRef.current = text;
    };

    const clearBox = () => {
      if (!hasValueRef.current && lastTextRef.current === EMPTY_TEXT) return;
      hasValueRef.current = false;
      lastIndexRef.current = -1;
      lastTimeRef.current = null;
      writeText(EMPTY_TEXT);
    };

    const processPending = () => {
      rafRef.current = null;
      const param = pendingParamRef.current;
      pendingParamRef.current = null;

      if (!param?.time) {
        clearBox();
        return;
      }

      const tNorm = normalizeTime(param.time);
      if (tNorm == null) {
        clearBox();
        return;
      }

      if (hasValueRef.current && tNorm === lastTimeRef.current) return;

      let candle = getParamCandle(param, series);
      let idx = -1;
      if (!candle) {
        const data = dataRef.current;
        idx = findClosestIndex(data, tNorm, lastIndexRef);
        if (idx < 0 || idx >= data.length) {
          clearBox();
          return;
        }
        candle = data[idx];
      }

      if (!candle) {
        clearBox();
        return;
      }

      const text = formatCandleText(candle);
      if (text === lastTextRef.current) {
        hasValueRef.current = true;
        lastTimeRef.current = tNorm;
        if (idx >= 0) lastIndexRef.current = idx;
        return;
      }

      hasValueRef.current = true;
      lastTimeRef.current = tNorm;
      if (idx >= 0) lastIndexRef.current = idx;
      writeText(text);
    };

    const handleCrosshairMove = (param) => {
      try {
        if (!param?.time) {
          if (hasValueRef.current || lastTextRef.current !== EMPTY_TEXT) {
            pendingParamRef.current = null;
            if (rafRef.current == null) rafRef.current = requestAnimationFrame(processPending);
          }
          return;
        }

        const tNorm = normalizeTime(param.time);
        if (hasValueRef.current && tNorm === lastTimeRef.current) return;
        if (pendingParamRef.current?.time && normalizeTime(pendingParamRef.current.time) === tNorm) {
          pendingParamRef.current = param;
          return;
        }

        pendingParamRef.current = param;
        if (rafRef.current == null) rafRef.current = requestAnimationFrame(processPending);
      } catch {}
    };

    try { chart.subscribeCrosshairMove(handleCrosshairMove); } catch {}

    return () => {
      try { chart.unsubscribeCrosshairMove(handleCrosshairMove); } catch {}
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      pendingParamRef.current = null;
    };
  }, [chart, series, dataRef]);

  return <div ref={boxRef} className="ohlc-box" style={BOX_STYLE}>{EMPTY_TEXT}</div>;
}

export default React.memo(OHLCBox);
